import React, { useLayoutEffect, useMemo, useRef, useState } from "react";

/** Duración del efecto de desaparición (ms). El botón se desmonta después. */
export const BUTTON_HIDE_EXIT_MS = 520;

const STYLE_ID = "er-btn-hide-countdown-css-v5";

export function ensureButtonHideCountdownStyles() {
	if (typeof document === "undefined") return;
	if (document.getElementById(STYLE_ID)) return;
	const el = document.createElement("style");
	el.id = STYLE_ID;
	el.textContent = `
@keyframes er-btn-hide-countdown-dash {
	from { stroke-dashoffset: 0; }
	to { stroke-dashoffset: 100; }
}
@keyframes er-btn-hide-shimmer {
	0% { opacity: 0.35; }
	50% { opacity: 0.7; }
	100% { opacity: 0.35; }
}
@keyframes er-btn-hide-exit {
	0% {
		opacity: 1;
		transform: scale(1);
		filter: blur(0) brightness(1);
	}
	35% {
		opacity: 1;
		transform: scale(1.04);
		filter: blur(0) brightness(1.35);
	}
	100% {
		opacity: 0;
		transform: scale(0.82);
		filter: blur(8px) brightness(1.6);
	}
}
@keyframes er-btn-hide-ring-exit {
	0% { opacity: 1; }
	100% { opacity: 0; }
}
.er-btn-hide-countdown-progress {
	animation-name: er-btn-hide-countdown-dash;
	animation-timing-function: linear;
	animation-fill-mode: forwards;
}
.er-btn-hide-shimmer-ring {
	animation: er-btn-hide-shimmer 1.6s ease-in-out infinite;
}
.er-btn-hide-exit-wrap {
	animation-name: er-btn-hide-exit;
	animation-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
	animation-fill-mode: forwards;
	transform-origin: center center;
	will-change: opacity, transform, filter;
}
.er-btn-hide-ring-exit {
	animation-name: er-btn-hide-ring-exit;
	animation-timing-function: ease-out;
	animation-fill-mode: forwards;
}
`;
	document.head.appendChild(el);
}

function parseRadiusPx(radiusCss, fallbackCap) {
	const cap = Math.max(0, fallbackCap);
	if (radiusCss == null || radiusCss === "") return Math.min(12, cap / 2);
	if (typeof radiusCss === "number" && Number.isFinite(radiusCss)) {
		return Math.max(0, Math.min(radiusCss, cap / 2));
	}
	const s = String(radiusCss).trim();
	const first = s.split(/\s+/)[0] || "";
	if (first.endsWith("%")) {
		const pct = Number.parseFloat(first);
		if (Number.isFinite(pct)) return Math.max(0, Math.min((pct / 100) * cap, cap / 2));
	}
	const n = Number.parseFloat(first);
	if (Number.isFinite(n)) return Math.max(0, Math.min(n, cap / 2));
	return Math.min(12, cap / 2);
}

/** Path de rect redondeado que EMPIEZA en el centro-arriba y sigue horario. */
function roundedRectPathFromTopCenter(x, y, w, h, radius) {
	const r = Math.max(0, Math.min(radius, w / 2, h / 2));
	const cx = x + w / 2;
	if (r < 0.5) {
		return `M ${cx} ${y} L ${x + w} ${y} L ${x + w} ${y + h} L ${x} ${y + h} L ${x} ${y} L ${cx} ${y}`;
	}
	return [
		`M ${cx} ${y}`,
		`L ${x + w - r} ${y}`,
		`A ${r} ${r} 0 0 1 ${x + w} ${y + r}`,
		`L ${x + w} ${y + h - r}`,
		`A ${r} ${r} 0 0 1 ${x + w - r} ${y + h}`,
		`L ${x + r} ${y + h}`,
		`A ${r} ${r} 0 0 1 ${x} ${y + h - r}`,
		`L ${x} ${y + r}`,
		`A ${r} ${r} 0 0 1 ${x + r} ${y}`,
		`L ${cx} ${y}`,
	].join(" ");
}

/**
 * Marco galaxy (blanco / plata) para hideAfterSeconds.
 * Al acabar la cuenta atrás, el botón hace un fade/blur de salida.
 */
export default function ButtonHideCountdownFrame({
	durationSec,
	borderRadiusCss,
	children,
	pad = 2.5,
}) {
	const wrapRef = useRef(null);
	const [size, setSize] = useState({ w: 0, h: 0 });
	const dur = Math.max(0, Number(durationSec) || 0);
	const exitSec = BUTTON_HIDE_EXIT_MS / 1000;
	const uidRef = useRef(`er-btn-hide-${Math.random().toString(36).slice(2, 9)}`);
	const uid = uidRef.current;
	const gradId = `${uid}-grad`;
	const glowId = `${uid}-glow`;

	useLayoutEffect(() => {
		ensureButtonHideCountdownStyles();
	}, []);

	useLayoutEffect(() => {
		const el = wrapRef.current;
		if (!el || !(dur > 0)) return undefined;
		const measure = () => {
			setSize({
				w: Math.max(0, el.offsetWidth || 0),
				h: Math.max(0, el.offsetHeight || 0),
			});
		};
		measure();
		const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
		if (ro) ro.observe(el);
		window.addEventListener("resize", measure);
		return () => {
			if (ro) ro.disconnect();
			window.removeEventListener("resize", measure);
		};
	}, [dur]);

	const rx = parseRadiusPx(borderRadiusCss, Math.min(size.w, size.h));
	const ringPath = useMemo(() => {
		if (!(size.w > 0 && size.h > 0)) return "";
		return roundedRectPathFromTopCenter(pad, pad, size.w, size.h, rx);
	}, [size.w, size.h, pad, rx]);

	if (!(dur > 0)) return children;

	const svgW = size.w + pad * 2;
	const svgH = size.h + pad * 2;
	const stroke = 2;

	return (
		<div
			ref={wrapRef}
			className="er-btn-hide-exit-wrap"
			style={{
				position: "relative",
				width: "100%",
				height: "100%",
				overflow: "visible",
				animationDuration: `${exitSec}s`,
				animationDelay: `${dur}s`,
			}}
		>
			{children}
			{size.w > 0 && size.h > 0 && ringPath ? (
				<svg
					aria-hidden
					className="er-btn-hide-ring-exit"
					width={svgW}
					height={svgH}
					viewBox={`0 0 ${svgW} ${svgH}`}
					style={{
						position: "absolute",
						left: -pad,
						top: -pad,
						width: svgW,
						height: svgH,
						pointerEvents: "none",
						overflow: "visible",
						zIndex: 2,
						animationDuration: `${Math.min(0.28, exitSec * 0.55)}s`,
						animationDelay: `${dur}s`,
					}}
				>
					<defs>
						<linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
							<stop offset="0%" stopColor="#ffffff" />
							<stop offset="45%" stopColor="#e8eef7" />
							<stop offset="100%" stopColor="#c7d2fe" />
						</linearGradient>
						<filter id={glowId} x="-50%" y="-50%" width="200%" height="200%">
							<feGaussianBlur stdDeviation="1.5" result="b" />
							<feMerge>
								<feMergeNode in="b" />
								<feMergeNode in="SourceGraphic" />
							</feMerge>
						</filter>
					</defs>

					<path
						d={ringPath}
						fill="none"
						stroke="rgba(15,23,42,0.35)"
						strokeWidth={stroke + 1.5}
					/>
					<path
						d={ringPath}
						fill="none"
						stroke="#ffffff"
						strokeOpacity={0.22}
						strokeWidth={stroke + 2.5}
					/>
					<path
						className="er-btn-hide-shimmer-ring"
						d={ringPath}
						fill="none"
						stroke={`url(#${gradId})`}
						strokeOpacity={0.55}
						strokeWidth={stroke}
						strokeDasharray="1.2 4.5"
						strokeLinecap="round"
					/>
					<path
						className="er-btn-hide-countdown-progress"
						d={ringPath}
						fill="none"
						stroke={`url(#${gradId})`}
						strokeWidth={stroke}
						strokeLinecap="round"
						strokeLinejoin="round"
						pathLength={100}
						strokeDasharray={100}
						strokeDashoffset={0}
						filter={`url(#${glowId})`}
						style={{ animationDuration: `${dur}s` }}
					/>
				</svg>
			) : null}
		</div>
	);
}
